import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TypesSelectStyle } from '../ColorSelector/TypesSelectStyle';
import CheckIcon from '../../assets/svgs/CheckIcon';
import ChevronIcon from '../../assets/svgs/ChevronIcon';
import { useSelector } from 'react-redux';
import translate from '../../libs/i18n';

const truncateText = (text, maxLength = 25) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
};

const CollapseGroupedItems = ({ show, groupedColors, handleSelect, selected, searchTerm }) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const language = useSelector((state) => state.settings.language);

  const toggleGroup = (groupKey) => {
    setExpandedGroups({
      ...expandedGroups,
      [groupKey]: !expandedGroups[groupKey]
    });
  };

  const getTranslatedName = (item) => {
    return translate(item.key, language);
  };

  const filterItemsBySearch = (items) => {
    if (!searchTerm || !searchTerm.trim()) {
      return items;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return items.filter((item) => {
      const translatedName = getTranslatedName(item).toLowerCase();
      return translatedName.includes(lowerSearchTerm);
    });
  };

  const getFilteredGroups = () => {
    if (!searchTerm || !searchTerm.trim()) {
      return groupedColors;
    }

    return groupedColors
      .map((group) => ({
        ...group,
        items: filterItemsBySearch(group.items),
      }))
      .filter((group) => group.items.length > 0);
  };

  const filteredGroups = getFilteredGroups();
  const hasResults = filteredGroups.some((group) => group.items.length > 0);

  return show && groupedColors?.length ? (
    <div className="types-list-container">
      {filteredGroups.length > 0 ? (
        filteredGroups.map((group) => {
          const sortedItems = [...group.items].sort((a, b) => {
            const nameA = getTranslatedName(a);
            const nameB = getTranslatedName(b);

            return nameA.localeCompare(nameB, "pt", {
              sensitivity: "base",
            });
          });

        return (
          <div key={group.groupKey} className="group-container">
            <div className="group-label" onClick={() => toggleGroup(group.groupKey)}>
              <strong>{translate(group.groupKey, language)}</strong>
            </div>
            {expandedGroups[group.groupKey] &&
              sortedItems.map((color) => (
                <button
                  key={color?.id}
                  className={`type-option${selected?.id === color?.id ? " selected" : ""}`}
                  type="button"
                  onClick={() =>
                    handleSelect(selected?.id !== color?.id ? color : null)
                  }
                >
                  <div
                    className="option-box"
                    style={{
                      backgroundColor: color?.color,
                      borderColor: color?.color,
                    }}
                  >
                    <CheckIcon />
                  </div>
                  <div className="option-name">{getTranslatedName(color)}</div>
                </button>
              ))}
          </div>
        );
      })
      ) : (
        <div className="no-results">
          {translate('noResults', language)}
        </div>
      )}
    </div>
  ) : null;
};

CollapseGroupedItems.propTypes = {
  show: PropTypes.bool.isRequired,
  groupedColors: PropTypes.arrayOf(
    PropTypes.shape({
      groupKey: PropTypes.string.isRequired,
      groupName: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          key: PropTypes.string.isRequired,
          color: PropTypes.string.isRequired
        })
      ).isRequired
    })
  ).isRequired,
  handleSelect: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  selected: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    key: PropTypes.string,
    color: PropTypes.string,
    valorRoi: PropTypes.number,
  }),
};

const ColorSelector = ({ groupedColors, selected, parent, placeholder, disabled, handleSelect }) => {
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const language = useSelector((state) => state.settings.language);

  useEffect(() => {
    if (selected) {
      setShow(false);
      setSearchTerm('');
    }
  }, [parent?.id, selected]);

  useEffect(() => {
    if (!show) {
      setSearchTerm('');
    }
  }, [show]);

  return (
    <TypesSelectStyle className={groupedColors?.length && !disabled && show ? 'show' : null}>
      {show ? (
        <div className="search-input-wrapper">
          <input
            type="text"
            className="color-search-input"
            placeholder={translate('searchClass', language)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
          <button
            type="button"
            className="icon"
            onClick={() => setShow(false)}
          >
            <ChevronIcon />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="show-hide-list"
          disabled={disabled}
          onClick={() => setShow(!show)}
        >
          {selected && selected?.color?.length && selected?.key?.length ? (
            <div className="show-hide-selected">
              <div
                className="selected-box"
                style={{
                  backgroundColor: selected?.color,
                  borderColor: selected?.color,
                }}
              >
                <CheckIcon />
              </div>
              <div className="selected-name" title={translate(selected?.key, language)}>
                {selected?.id === parent?.id
                  ? 'Any'
                  : truncateText(translate(selected?.key, language))}
              </div>
            </div>
          ) : (
            <div className="show-hide-unselected">
              {placeholder || 'Select Type'}
            </div>
          )}
          <span className="icon">
            <ChevronIcon />
          </span>
        </button>
      )}
      <CollapseGroupedItems 
        show={show} 
        groupedColors={groupedColors} 
        handleSelect={handleSelect} 
        selected={selected}
        searchTerm={searchTerm}
      />
    </TypesSelectStyle>
  );
};

ColorSelector.propTypes = {
  groupedColors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number,
      groupName: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      groupKey: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          color: PropTypes.string.isRequired,
          valorRoi: PropTypes.number.isRequired,
          id: PropTypes.number.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  selected: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    key: PropTypes.string,
    color: PropTypes.string,
    valorRoi: PropTypes.number,
  }),
  parent: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    key: PropTypes.string,
    color: PropTypes.string,
    valorRoi: PropTypes.number,
  }),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  handleSelect: PropTypes.func.isRequired,
};

ColorSelector.defaultProps = {
  groupedColors: [],
  selected: null,
  parent: null,
  placeholder: '',
  disabled: false,
};

export default ColorSelector;
